import { QueryClient } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import styled from 'styled-components';

import LandingPageHome from '../components/LandingPageHome';
import { NextPageWithLayout } from '../components/layouts';
import LandingPageLayout from '../components/layouts/LandingPageLayout';
import useRoutePrefetch from '../hooks/useRoutePrefetch';
import { DefaultLocale } from '../providers/tolgee';
import APILogger from '../services/APILogger';
import CoreAPIService from '../services/CoreAPIService';
import { getErroredSessionSsrRedirect } from '../utils/erroredSessionRedirectSsr';
import { generateUniqueId } from '../utils/general';
import { forwardedHomeQuery } from '../utils/homeRedirectQuery';
import {
  detectAndBestEffortSetLanguage,
  getLocaleRedirectIfNeeded,
} from '../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../utils/react-query-utils';
import { getRotatingServerSession } from '../utils/serverSession';

const Home: NextPageWithLayout = () => {
  useRoutePrefetch('/[tenantId]/home');
  useRoutePrefetch('/[tenantId]/lessons');
  useRoutePrefetch('/[tenantId]/workspace/[workspaceId]');

  return (
    <Container>
      <LandingPageLayout
        title="Curipod - Writing Practice Students Love"
        subTitle="Teacher led lessons aligned to your district's curriculum. Students read, write and discuss. Not alone on the screen. Together."
        locale={DefaultLocale}
        hideBlobs={true}
        disableHeadline={true}
      >
        <LandingPageHome />
      </LandingPageLayout>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
`;

export const getServerSideProps: GetServerSideProps = async context => {
  const logger = new APILogger();
  logger.setRequestId(
    (context.req.headers['x-cloud-trace-context'] as string) ||
      generateUniqueId().toString(),
  );
  try {
    // Read directly, with no timeout race. This used to be raced against a 20s
    // timeout because `getSession({ req })` made an *unbounded* HTTP round-trip to
    // the public NEXTAUTH_URL (NEXTAUTH_URL_INTERNAL is unset), so a hung self-call
    // could pin this page open. `getRotatingServerSession` runs in-process, and the
    // only I/O left on a session read is the refresh POST — now bounded at 10s — so
    // there is nothing unbounded left to guard.
    //
    // The race was also unsafe to keep: `getServerSession` writes the rotated
    // `Set-Cookie` onto `res` *before* it resolves (next-auth/next/index.js — the
    // `cookies.forEach(setCookie)` precedes `return body`). A timeout could not
    // cancel that, so the losing branch would render logged-out while the winning
    // rotation had already mutated the response — orphaning the successor token,
    // which is the exact bug this PR exists to fix.
    const session = await getRotatingServerSession(context.req, context.res);

    // An errored-but-authenticated session (ProvisioningError has no usable user;
    // RefreshAccessTokenError / a token-less session are unusable too) must not be
    // forwarded into the app: clear its cookie server-side and redirect to the
    // styled error page or /login. Otherwise the client BackendServiceProvider
    // recovery signs the user out, and forwarding them to /{tenantId}/home loops.
    const erroredRedirect = getErroredSessionSsrRedirect(session, context);
    if (erroredRedirect) return erroredRedirect;

    if (session) {
      const { activeTenantId } = await CoreAPIService.fetchActiveTenantId(
        session.user.uid,
        logger,
      );

      // Forward allowlisted query params so post-signup deep links (e.g.
      // /?selectedCardId=activity-phonics-wizard from the phonics SEO pages)
      // survive the tenant-aware redirect into the user's home.
      const forwarded = forwardedHomeQuery(context.query);

      return {
        redirect: {
          permanent: false,
          destination: `/${activeTenantId}/home${forwarded}`,
        },
      };
    }

    // Non-authenticated: redirect non-default locale users to their localized landing.
    // Must come after the session check to avoid a redirect loop with /lang/[locale]/
    // which redirects authenticated users back to /.
    const localeRedirect = getLocaleRedirectIfNeeded(context, '/', { logger });
    if (localeRedirect) return localeRedirect;

    const queryClient = new QueryClient();

    return {
      props: {
        dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
        ...(await detectAndBestEffortSetLanguage(context, {
          routeClass: 'nocache',
          logger,
        })),
      },
    };
  } catch (error) {
    logger.error('INDEX_PAGE_GSSP_ERROR', {
      error: error instanceof Error ? error.message : JSON.stringify(error),
    });

    // On session check failure, still redirect non-default locale users.
    const localeRedirect = getLocaleRedirectIfNeeded(context, '/', { logger });
    if (localeRedirect) return localeRedirect;

    return {
      props: {
        ...(await detectAndBestEffortSetLanguage(context, {
          routeClass: 'nocache',
          logger,
        })),
      },
    };
  }
};

export default Home;
